import React, {
  ReactElement,
  useContext,
  useMemo,
  useState
} from 'react';
import { PopupContext } from '../../Popup';

export interface MassUpdateService<T = number> {
  toggleEnabled: () => void,
  isEnabled: () => boolean,
  toggleChecked: (id: T) => void,
  isChecked: (id: T) => boolean,
  getCheckedIds: () => T[],
  reset: () => void,
  setIds: (ids: T[]) => void,
  selectAll: (resetIfAllSelected?: boolean) => void,
  areAllSelected: () => boolean,
}

const MassUpdateContext = React.createContext<MassUpdateService | null>(null);

export interface MassUpdateProps {
  children: ReactElement | ReactElement[],
  enabled?: boolean,
}

export function useMassUpdateService<T = number>(): MassUpdateService<T> | null {
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-expect-error
  return useContext<MassUpdateService<T>>(MassUpdateContext);
}

export function useMassUpdateServiceRequired<T = number>(): MassUpdateService<T> {
  const service = useMassUpdateService<T>();

  if (!service) {
    throw new Error('MassUpdateService is not provided');
  }

  return service;
}

export function MassUpdate<T = number>(props: MassUpdateProps) {
  const [value, setValue] = useState<Map<T, boolean>>(new Map());
  const [selectAllValue, setSelectAllValue] = useState<Map<T, boolean>>(new Map());
  const [enabled, setEnabled] = useState<boolean>(props.enabled || false);
  const [i, setI] = useState<number>(0);

  const refreshUi = () => {
    const newValue = i + 1;
    setI(newValue);
  };

  const reset = () => {
    setValue(new Map());
  };

  const toggleEnabled = () => {
    const newValue = !enabled;

    if (!newValue) {
      reset();
    }

    setEnabled(newValue);
  };

  const isEnabled = () => {
    return enabled;
  };

  const toggleChecked = (id: T) => {
    const newValue = new Map(value);
    const currentValue = newValue.get(id);

    if (currentValue) {
      newValue.delete(id);
    } else {
      newValue.set(id, true);
    }

    setValue(newValue);
    refreshUi();
  };

  const isChecked = (id: T) => value.get(id) || false;

  const getCheckedIds = () => {
    return Array.from(value.keys());
  };

  function updateCheckedItems(result: Map<T, boolean>) {
    const newValue = new Map(value);
    for (const [id] of newValue) {
      if (!result.has(id)) {
        newValue.delete(id);
      }
    }
    setValue(newValue);
  }

  const setIds = (ids: T[]) => {
    const result = new Map<T, boolean>();
    ids.forEach((id) => {
      result.set(id, true);
    });

    refreshUi();
    setSelectAllValue(result);
    updateCheckedItems(result);
  };

  const areAllSelected = () => {
    return value.size > 0 && value.size === selectAllValue.size;
  };

  const selectAll = (resetIfAllSelected = true) => {
    if (areAllSelected() && resetIfAllSelected) {
      reset();
    } else {
      setValue(new Map(selectAllValue));
    }

    refreshUi();
  };

  const contextValue = useMemo(() => ({
    toggleEnabled,
    isEnabled,
    isChecked,
    toggleChecked,
    getCheckedIds,
    reset,
    setIds,
    selectAll,
    areAllSelected,
  }), [enabled, i]);

  return (
    <MassUpdateContext.Provider
      /* eslint-disable-next-line @typescript-eslint/ban-ts-comment */
      // @ts-expect-error
      value={contextValue}
    >
      <PopupContext>
        {props.children}
      </PopupContext>
    </MassUpdateContext.Provider>
  );
}
